function normalizeNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function normalizePositiveNumber(value) {
  const numericValue = normalizeNumber(value);
  return numericValue !== null && numericValue > 0 ? numericValue : null;
}

function normalizeString(value) {
  const normalizedValue = String(value || '').trim();
  return normalizedValue || '';
}

function normalizeSide(value) {
  const side = normalizeString(value).toLowerCase();
  return side || null;
}

function createPlaceholderParticipant(matchKey, index, placeholderName) {
  return {
    key: `${matchKey}:placeholder:${index}`,
    athleteId: null,
    roundId: null,
    name: placeholderName,
    image: null,
    rawScore: null,
    isWinner: false,
    isPlaceholder: true,
    sourceRound: null,
    slotOrder: null,
    seedNumber: null,
    rawOrder: Number(index) || 0,
  };
}

function createParticipant(round, matchKey, rawOrder = 0) {
  const bracket = round?.vs_bracket || null;

  return {
    key: `${matchKey}:${normalizePositiveNumber(round?.round_id) || rawOrder}`,
    athleteId: normalizePositiveNumber(round?.id),
    roundId: normalizePositiveNumber(round?.round_id),
    name: normalizeString([round?.name, round?.surname].filter(Boolean).join(' ')),
    image: round?.img || null,
    rawScore: normalizeNumber(round?.total_score),
    isWinner: round?.is_vs_winner === true || Number(round?.is_vs_winner) === 1,
    isPlaceholder: false,
    sourceRound: round,
    slotOrder: normalizeNumber(bracket?.slot_order),
    seedNumber: normalizeNumber(bracket?.seed_number),
    rawOrder,
  };
}

function compareParticipants(left, right) {
  if (left.isPlaceholder !== right.isPlaceholder) {
    return Number(left.isPlaceholder) - Number(right.isPlaceholder);
  }

  const leftSlotOrder = normalizeNumber(left?.slotOrder);
  const rightSlotOrder = normalizeNumber(right?.slotOrder);

  if (leftSlotOrder !== null && rightSlotOrder !== null && leftSlotOrder !== rightSlotOrder) {
    return leftSlotOrder - rightSlotOrder;
  }

  if ((leftSlotOrder !== null) !== (rightSlotOrder !== null)) {
    return leftSlotOrder !== null ? -1 : 1;
  }

  const leftSeedNumber = normalizeNumber(left?.seedNumber);
  const rightSeedNumber = normalizeNumber(right?.seedNumber);

  if (leftSeedNumber !== null && rightSeedNumber !== null && leftSeedNumber !== rightSeedNumber) {
    return leftSeedNumber - rightSeedNumber;
  }

  if ((leftSeedNumber !== null) !== (rightSeedNumber !== null)) {
    return leftSeedNumber !== null ? -1 : 1;
  }

  const leftAthleteId = normalizePositiveNumber(left?.athleteId);
  const rightAthleteId = normalizePositiveNumber(right?.athleteId);

  if (leftAthleteId !== null && rightAthleteId !== null && leftAthleteId !== rightAthleteId) {
    return leftAthleteId - rightAthleteId;
  }

  return Number(left?.rawOrder || 0) - Number(right?.rawOrder || 0);
}

function normalizeParticipants(participants, matchKey, placeholderName) {
  const deduped = [];
  const participantsBySlot = new Map();
  const participantsByAthlete = new Map();

  (participants || []).forEach((participant) => {
    const slotOrder = normalizeNumber(participant?.slotOrder);
    const slotKey = slotOrder !== null ? `slot:${slotOrder}` : null;
    const athleteId = normalizePositiveNumber(participant?.athleteId);
    const athleteKey = athleteId !== null
      ? `athlete:${athleteId}`
      : `name:${normalizeString(participant?.name).toLowerCase()}`;
    const existing = (slotKey && participantsBySlot.get(slotKey)) || participantsByAthlete.get(athleteKey);

    if (!existing) {
      deduped.push(participant);

      if (slotKey) {
        participantsBySlot.set(slotKey, participant);
      }
      participantsByAthlete.set(athleteKey, participant);
      return;
    }

    const existingRoundId = normalizePositiveNumber(existing?.roundId);
    const candidateRoundId = normalizePositiveNumber(participant?.roundId);
    const shouldReplace = candidateRoundId !== null
      && (existingRoundId === null || candidateRoundId > existingRoundId);

    if (!shouldReplace) {
      return;
    }

    const existingIndex = deduped.indexOf(existing);
    if (existingIndex >= 0) {
      deduped.splice(existingIndex, 1, participant);
    }

    if (slotKey) {
      participantsBySlot.set(slotKey, participant);
    }
    participantsByAthlete.set(athleteKey, participant);
  });

  const normalized = [...deduped].sort(compareParticipants);

  while (normalized.length < 2) {
    normalized.push(createPlaceholderParticipant(matchKey, normalized.length, placeholderName));
  }

  return normalized.slice(0, 2);
}

function compareMatches(left, right) {
  const leftDisplayOrder = normalizeNumber(left?.displayOrder);
  const rightDisplayOrder = normalizeNumber(right?.displayOrder);

  if (leftDisplayOrder !== null && rightDisplayOrder !== null && leftDisplayOrder !== rightDisplayOrder) {
    return leftDisplayOrder - rightDisplayOrder;
  }

  const leftMatchNumber = normalizeNumber(left?.matchNumber);
  const rightMatchNumber = normalizeNumber(right?.matchNumber);

  if (leftMatchNumber !== null && rightMatchNumber !== null && leftMatchNumber !== rightMatchNumber) {
    return leftMatchNumber - rightMatchNumber;
  }

  return String(left?.key || '').localeCompare(String(right?.key || ''));
}

function resolveSideForMatches(matches) {
  const orderedGroups = [];

  matches.forEach((match) => {
    const group = normalizeString(match?.group);
    if (!group || group === 'F' || group === '3P' || orderedGroups.includes(group)) {
      return;
    }

    orderedGroups.push(group);
  });

  const boundary = Math.ceil(orderedGroups.length / 2);
  const sideMap = new Map(orderedGroups.map((group, index) => [group, index < boundary ? 'left' : 'right']));

  return matches.map((match) => {
    if (match.side) {
      return match;
    }

    if (match.group === 'F' || match.group === '3P') {
      return { ...match, side: 'center' };
    }

    return {
      ...match,
      side: sideMap.get(match.group) || null,
    };
  });
}

export function assembleVsBracketMatches(rounds = [], options = {}) {
  const stageId = options.stageId ?? 'stage';
  const placeholderName = options.placeholderName || 'TBD';
  const matches = new Map();

  (rounds || []).forEach((round, index) => {
    if (!round || typeof round !== 'object') {
      return;
    }

    const bracket = round?.vs_bracket || null;
    const group = normalizeString(bracket?.group || round?.vs_group);
    const matchNumber = normalizeNumber(bracket?.match_number ?? round?.vs_match_number);
    const displayOrder = normalizeNumber(bracket?.display_order);
    const side = normalizeSide(bracket?.side);
    const block = normalizeString(bracket?.block);
    const matchKey = String(bracket?.match_key || `${stageId}:${group || 'nogroup'}:${matchNumber ?? normalizePositiveNumber(round?.round_id) ?? index + 1}`);

    if (!matches.has(matchKey)) {
      matches.set(matchKey, {
        key: matchKey,
        group,
        matchNumber,
        displayOrder,
        side,
        block,
        participants: [],
      });
    }

    const existingMatch = matches.get(matchKey);
    if (existingMatch.displayOrder === null && displayOrder !== null) {
      existingMatch.displayOrder = displayOrder;
    }
    if (!existingMatch.side && side) {
      existingMatch.side = side;
    }
    if (!existingMatch.block && block) {
      existingMatch.block = block;
    }

    existingMatch.participants.push(createParticipant(round, matchKey, index));
  });

  const sortedMatches = Array.from(matches.values())
    .map((match) => ({
      ...match,
      participants: normalizeParticipants(match.participants, match.key, placeholderName),
    }))
    .sort(compareMatches);

  return resolveSideForMatches(sortedMatches);
}